/**
 * QMS Comments Component
 * Displays comment thread for a QMS ticket with add/edit/delete functionality
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Send, MessageSquare, Edit2, Trash2, X, Check } from 'lucide-react';
import { useQmsComments, useAddQmsComment, useEditQmsComment, useDeleteQmsComment } from '../../hooks/useQmsComments';
import { useSession } from '@/hooks/useSession';
import { isPopulatedUser } from '../../types/qms.types';
import type { QmsComment } from '../../types/qms.types';

interface QmsCommentsProps {
  ticketId: string;
}

export function QmsComments({ ticketId }: QmsCommentsProps) {
  const { t } = useTranslation('qms');
  const { user } = useSession();
  const { data: comments, isLoading, error } = useQmsComments(ticketId);
  const addCommentMutation = useAddQmsComment();
  const editCommentMutation = useEditQmsComment();
  const deleteCommentMutation = useDeleteQmsComment();

  const [newComment, setNewComment] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newComment.trim()) return;

    try {
      await addCommentMutation.mutateAsync({
        ticketId,
        content: newComment.trim(),
      });
      setNewComment('');
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const handleStartEdit = (comment: QmsComment) => {
    setEditingId(comment._id);
    setEditContent(comment.content);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditContent('');
  };

  const handleSaveEdit = async (comment: QmsComment) => {
    if (!editContent.trim()) return;

    try {
      await editCommentMutation.mutateAsync({
        ticketId,
        noteId: comment._id,
        content: editContent.trim(),
      });
      handleCancelEdit();
    } catch (error) {
      console.error('Failed to edit comment:', error);
    }
  };

  const handleDelete = async (comment: QmsComment) => {
    if (!confirm(t('confirmations.deleteComment'))) return;

    try {
      await deleteCommentMutation.mutateAsync({
        ticketId,
        noteId: comment._id,
      });
    } catch (error) {
      console.error('Failed to delete comment:', error);
    }
  };

  const getCommenterName = (comment: QmsComment): string => {
    if (comment.user_name) return comment.user_name;
    if (isPopulatedUser(comment.user)) {
      return `${comment.user.firstname} ${comment.user.lastname}`;
    }
    return t('comments.unknownUser');
  };

  const getCommenterInitials = (comment: QmsComment): string => {
    if (comment.user_name) {
      const parts = comment.user_name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return comment.user_name[0]?.toUpperCase() || '?';
    }
    if (isPopulatedUser(comment.user)) {
      const first = comment.user.firstname?.[0] || '';
      const last = comment.user.lastname?.[0] || '';
      return (first + last).toUpperCase();
    }
    return '?';
  };

  const isOwnComment = (comment: QmsComment): boolean => {
    if (!user) return false;
    const commentUserId = isPopulatedUser(comment.user) ? comment.user._id : comment.user;
    return String(commentUserId) === String(user._id);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          {t('comments.title')}
        </h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-atek-blue"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          {t('comments.title')}
        </h3>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {t('comments.loadError')}
        </div>
      </div>
    );
  }

  const sortedComments = [...(comments || [])].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <MessageSquare className="h-5 w-5" />
        {t('comments.title')}
        {comments && comments.length > 0 && (
          <span className="text-sm font-normal text-gray-500">({comments.length})</span>
        )}
      </h3>

      {/* Add Comment Form */}
      <form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-4">
        <div className="flex gap-3">
          {/* User Avatar */}
          <div className="flex-shrink-0">
            <div className="w-8 h-8 rounded-full bg-atek-blue text-white flex items-center justify-center text-sm font-medium">
              {user?.firstname?.[0]?.toUpperCase() || '?'}
              {user?.lastname?.[0]?.toUpperCase() || ''}
            </div>
          </div>

          {/* Comment Input */}
          <div className="flex-1">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={t('comments.placeholder')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue focus:border-transparent resize-none"
            />
            <div className="flex justify-end mt-2">
              <button
                type="submit"
                disabled={!newComment.trim() || addCommentMutation.isPending}
                className="px-4 py-2 bg-atek-blue text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Send className="h-4 w-4" />
                {addCommentMutation.isPending ? t('comments.posting') : t('comments.postComment')}
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Comments List */}
      {sortedComments.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-400" />
          <p>{t('comments.noComments')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedComments.map((comment) => (
            <div key={comment._id} className="flex gap-3 bg-white rounded-lg p-4 border border-gray-200">
              {/* Commenter Avatar */}
              <div className="flex-shrink-0">
                <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center text-sm font-medium">
                  {getCommenterInitials(comment)}
                </div>
              </div>

              {/* Comment Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-medium text-gray-900">
                      {getCommenterName(comment)}
                    </span>
                    <span className="text-sm text-gray-500">
                      {format(new Date(comment.date), 'MMM d, yyyy • h:mm a')}
                    </span>
                    {comment.edited_at && (
                      <span className="text-xs text-gray-400 italic">
                        ({t('comments.edited')})
                      </span>
                    )}
                  </div>

                  {/* Edit/Delete Actions (only for own comments when not editing) */}
                  {isOwnComment(comment) && editingId !== comment._id && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleStartEdit(comment)}
                        className="p-1 text-gray-400 hover:text-atek-blue transition-colors"
                        title={t('comments.edit')}
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(comment)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        title={t('comments.delete')}
                        disabled={deleteCommentMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* View Mode */}
                {editingId !== comment._id ? (
                  <p className="text-gray-700 whitespace-pre-wrap break-words">
                    {comment.content}
                  </p>
                ) : (
                  /* Edit Mode */
                  <div className="space-y-2">
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue focus:border-transparent resize-none"
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={handleCancelEdit}
                        className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors flex items-center gap-1"
                      >
                        <X className="h-4 w-4" />
                        {t('comments.cancel')}
                      </button>
                      <button
                        onClick={() => handleSaveEdit(comment)}
                        disabled={!editContent.trim() || editCommentMutation.isPending}
                        className="px-3 py-1.5 text-sm bg-atek-blue text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        <Check className="h-4 w-4" />
                        {editCommentMutation.isPending ? t('comments.saving') : t('comments.save')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
